import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import useGetTaskFromTemplatePreview from '@/api/queries/tasks/useGetTaskFromTemplatePreview';
import CreateEditTaskSidebar, {
  CreateEditTaskFormDefaultValues,
} from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import { encodeAssignedTo } from '@/helpers/taskAssignee';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { FC, useEffect } from 'react';
import { CommitmentItem } from './CommitmentsPanel';

type TaskCommitmentItem = Extract<
  CommitmentItem,
  { __typename: 'TaskCommitment' }
>;

const ArrangeTaskModal: FC<{
  commitment: TaskCommitmentItem;
  projectId?: number;
  onClose: () => void;
  onSuccess: (taskId: number) => void;
}> = (props) => {
  const { commitment, projectId, onClose, onSuccess } = props;

  // The preview query needs the project's public id; derive it from the
  // numeric id (this query is shared/deduped with the sidebar below).
  const { project } = useGetProjectByIdOverview(projectId?.toString());

  // What the task would be, resolved against this project (assignee /
  // deadline). The form reads solely from this — it is editable before saving.
  const { preview, isError, isLoading } = useGetTaskFromTemplatePreview({
    templateId: commitment.template.id,
    projectId: project?.publicId,
  });

  // On failure the query's global handler toasts; close rather than hang.
  useEffect(() => {
    if (isError) onClose();
  }, [isError, onClose]);

  if (isError) return null;

  // Feedback while resolving, so the form's prefilled defaults land on mount.
  if (isLoading || !preview) return <LoaderOverlay />;

  const name = preview.name || 'task';
  const base = {
    notes: preview.notes || '',
    // An unresolvable template assignee leaves the field out, so the sidebar's
    // own default applies rather than forcing the task unassigned.
    ...(preview.assignedTo
      ? { assignedTo: encodeAssignedTo(preview.assignedTo) }
      : {}),
    ...(preview.deadlineDate
      ? { dueDate: new Date(preview.deadlineDate) }
      : {}),
  };

  let defaultValues: CreateEditTaskFormDefaultValues;
  if (preview.__typename === 'ChecklistTaskFromTemplatePreview') {
    defaultValues = {
      ...base,
      type: 'checklist',
      name,
      checklistItems: (preview.checklistItems ?? []).map((label) => ({
        label,
      })),
    };
  } else if (
    preview.__typename === 'FormTaskFromTemplatePreview' ||
    preview.__typename === 'LegacyFormTaskFromTemplatePreview'
  ) {
    defaultValues = {
      ...base,
      type: 'form',
      formId: preview.formTemplate?.id ? +preview.formTemplate.id : undefined,
    };
  } else {
    defaultValues = { ...base, type: 'checklist', name, checklistItems: [] };
  }

  return (
    <CreateEditTaskSidebar
      isOpen={true}
      onClose={onClose}
      projectId={projectId}
      defaultValues={defaultValues}
      onSuccess={onSuccess}
    />
  );
};

export default ArrangeTaskModal;
